// 「資料をつくる」画面。docs/prototype.html の同タブをReact+TSXに移植。
// PDFドロップ → テキスト抽出(スキャンならOCR) → AI自動振り分け → 編集 → プレビュー → 保存 → 配信。
import { useEffect, useRef, useState } from "react";
import { addDoc, collection, doc, serverTimestamp, setDoc } from "firebase/firestore";
import { firestore } from "../../lib/firebase";
import { useAuth } from "../../lib/useAuth";
import { apiGet, apiPost, ApiError } from "../../lib/api";
import { extractPdfText, fileToBase64 } from "../../lib/pdf";
import { ruleClassify } from "../../lib/ruleClassify";
import { MaterialSheet } from "../../components/MaterialSheet";
import { CATEGORIES } from "../../types";
import type { Category, ClassifyResponse, Company, Scope, Urgency } from "../../types";

const emptyForm = {
  title: "",
  category: CATEGORIES[4] as Category,
  urgency: "低" as Urgency,
  date: "",
  summary: "",
  points: "",
  actions: "",
  target: "",
  deadline: "",
  contact: "",
  org: "",
  body: "",
};

export function CreateMaterial() {
  const { user } = useAuth();
  const [rawText, setRawText] = useState("");
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfStatus, setPdfStatus] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [scope, setScope] = useState<Scope>("common");
  const [companies, setCompanies] = useState<Company[]>([]);
  const [checkedCompanyIds, setCheckedCompanyIds] = useState<Set<string>>(new Set());
  const [savedMaterialId, setSavedMaterialId] = useState<string | null>(null);
  const [savedFallback, setSavedFallback] = useState(false);
  const [toast, setToast] = useState("");
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  // スキャンPDFのOCR用に先行アップロード済みの場合、保存時に二重アップロードしないよう覚えておく
  const [uploadedPdf, setUploadedPdf] = useState<{ path: string; name: string } | null>(null);

  useEffect(() => {
    apiGet<{ companies: Company[] }>("/api/companies")
      .then((r) => setCompanies(r.companies))
      .catch(() => void 0);
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2400);
  };

  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

  // スキャンPDF(テキスト抽出不可)のOCR。100ページを超える文書もあるため、Visionの非同期API
  // (実質ページ数無制限)でジョブを開始し、完了するまでポーリングする。
  const runOcr = async (file: File) => {
    setPdfStatus("PDFをアップロード中…");
    const base64 = await fileToBase64(file);
    const uploaded = await apiPost<{ path: string; name: string }>("/api/upload", {
      fileBase64: base64,
      fileName: file.name,
    });
    setUploadedPdf(uploaded);

    setPdfStatus("OCRジョブを開始しています…");
    const job = await apiPost<{ operationName: string; outputPrefix: string }>("/api/ocr", {
      storagePath: uploaded.path,
    });

    const maxAttempts = 120; // 3秒間隔で最大6分待つ(100ページ超の大きな文書向け)
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      setPdfStatus(`OCR処理中…(ページ数が多いと数分かかることがあります / ${attempt * 3}秒経過)`);
      await sleep(3000);
      const status = await apiGet<{ done: boolean; text?: string; error?: string }>(
        `/api/ocr/status?operation=${encodeURIComponent(job.operationName)}&outputPrefix=${encodeURIComponent(job.outputPrefix)}`
      );
      if (status.done) {
        if (status.error) {
          setPdfStatus(`OCRに失敗しました: ${status.error}`);
          return;
        }
        setRawText(status.text || "");
        setPdfStatus(`OCRで${(status.text || "").length}文字を抽出`);
        return;
      }
    }
    setPdfStatus("OCRの完了待ちがタイムアウトしました。時間を置いて再度お試しください");
  };

  const handleFiles = async (files: FileList | File[]) => {
    const file = Array.from(files).find((f) => f.type === "application/pdf");
    if (!file) return;
    setPdfFile(file);
    setUploadedPdf(null); // 新しいファイルなので前回のアップロード状態は破棄
    setPdfStatus("読み込み中…");
    try {
      const res = await extractPdfText(file);
      if (res.scanned) {
        await runOcr(file);
      } else {
        setRawText(res.text);
        setPdfStatus(`${res.pages}ページ / ${res.text.length}文字を抽出`);
      }
    } catch {
      setPdfStatus("PDFの読み込みに失敗しました");
    }
  };

  const handleAnalyze = async () => {
    const text = rawText.trim();
    if (!text) {
      showToast("先に情報を入力してください");
      return;
    }
    setAnalyzing(true);
    try {
      let result: ClassifyResponse;
      try {
        result = await apiPost<ClassifyResponse>("/api/classify", { text });
      } catch {
        result = { ...ruleClassify(text), fallback: true };
      }
      setSavedFallback(result.fallback);
      setForm({
        title: result.title,
        category: result.category,
        urgency: result.urgency,
        date: result.date,
        summary: result.summary,
        points: result.points.join("\n"),
        actions: result.actions.join("\n"),
        target: result.target,
        deadline: result.deadline,
        contact: result.contact,
        org: form.org,
        body: form.body,
      });
      if (result.fallback) showToast("AI判定に失敗したため簡易判定(ルールベース)を使用しました");
      else showToast("AIが内容を整理しました。確認・修正して保存できます");
    } finally {
      setAnalyzing(false);
    }
  };

  const toggleCompany = (id: string) => {
    setCheckedCompanyIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const currentMaterialDraft = () => ({
    title: form.title.trim() || "無題",
    category: form.category,
    urgency: form.urgency,
    date: form.date,
    summary: form.summary.trim(),
    points: form.points.split("\n").map((s) => s.trim()).filter(Boolean),
    actions: form.actions.split("\n").map((s) => s.trim()).filter(Boolean),
    target: form.target.trim(),
    deadline: form.deadline.trim(),
    contact: form.contact.trim(),
    org: form.org.trim(),
    body: form.body.trim(),
  });

  const handleSave = async () => {
    if (!user) return;
    const draft = currentMaterialDraft();
    if (!draft.summary && !draft.points.length && !draft.actions.length && !draft.body) {
      showToast("掲示する内容が空です");
      return;
    }
    const companyIds = scope === "common" ? companies.map((c) => c.id) : [...checkedCompanyIds];
    if (scope === "targeted" && !companyIds.length) {
      showToast("「選択した会社のみ」です。配信先の会社にチェックを入れてください");
      return;
    }

    setSaving(true);
    try {
      let sourcePdfPath: string | undefined;
      let sourcePdfName: string | undefined;
      if (uploadedPdf) {
        // スキャンPDFでOCR時に既にアップロード済み。二重アップロードしない。
        sourcePdfPath = uploadedPdf.path;
        sourcePdfName = uploadedPdf.name;
      } else if (pdfFile) {
        const base64 = await fileToBase64(pdfFile);
        const uploaded = await apiPost<{ path: string; name: string }>("/api/upload", {
          fileBase64: base64,
          fileName: pdfFile.name,
        });
        sourcePdfPath = uploaded.path;
        sourcePdfName = uploaded.name;
      }

      const payload = {
        ...draft,
        rawText,
        scope,
        companyIds,
        sourcePdfPath: sourcePdfPath ?? null,
        sourcePdfName: sourcePdfName ?? null,
        published: false,
        createdBy: user.uid,
        updatedAt: serverTimestamp(),
      };

      let materialId = savedMaterialId;
      if (materialId) {
        await setDoc(doc(firestore, "materials", materialId), payload, { merge: true });
      } else {
        const ref = await addDoc(collection(firestore, "materials"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        materialId = ref.id;
        setSavedMaterialId(materialId);
      }
      showToast("保存しました。配信先を確認して配信してください");
    } catch (e) {
      showToast(e instanceof ApiError ? e.message : "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!savedMaterialId) return;
    setPublishing(true);
    try {
      const res = await apiPost<{ results: { companyId: string; ok: boolean; error?: string }[]; note?: string }>(
        "/api/publish",
        { materialId: savedMaterialId, portalBaseUrl: window.location.origin }
      );
      if (res.note) showToast(res.note);
      else {
        const okCount = res.results.filter((r) => r.ok).length;
        showToast(`配信しました(成功 ${okCount}/${res.results.length}社)`);
      }
    } catch (e) {
      showToast(e instanceof ApiError ? e.message : "配信に失敗しました");
    } finally {
      setPublishing(false);
    }
  };

  const preview = currentMaterialDraft();

  return (
    <div className="grid">
      <div className="card no-print">
        <h2>
          <span className="ic">📥</span>1. 情報を入力(PDF)
        </h2>
        <div className="banner">
          外部から届いた<b>PDF</b>(製薬会社の案内・回収情報・行政通知など)をドロップすると、テキストを自動抽出してAIが日付・分類・要約を割り振ります。テキスト貼り付けも併用可。
        </div>
        <div
          className={`dropzone${dragOver ? " drag" : ""}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files);
          }}
        >
          <div className="dz-icon">📄</div>
          <div className="dz-main">PDFをここにドロップ、またはクリックして選択</div>
          <div className="dz-sub">複数ページ対応</div>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            style={{ display: "none" }}
            onChange={(e) => e.target.files && handleFiles(e.target.files)}
          />
        </div>
        {pdfStatus && <div className="hint">{pdfStatus}</div>}

        <label className="f">抽出テキスト(AIへの入力・編集可)</label>
        <textarea value={rawText} onChange={(e) => setRawText(e.target.value)} style={{ minHeight: 140 }} />
        <div className="row" style={{ marginTop: 12 }}>
          <button className="btn" onClick={handleAnalyze} disabled={analyzing}>
            <span>🤖</span>
            {analyzing ? "解析中…" : "AIで自動振り分け"}
          </button>
        </div>

        <div className="ai-box" style={{ marginTop: 16 }}>
          <div className="ttl">
            🤖 AIが構造化した掲示内容(編集できます)
            {savedFallback && <span className="chip u-中" style={{ marginLeft: 8 }}>簡易判定</span>}
          </div>
          <label className="f">タイトル</label>
          <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <div className="row" style={{ marginTop: 10 }}>
            <div style={{ flex: 1, minWidth: 150 }}>
              <label className="f">分類</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as Category })}>
                {CATEGORIES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 110 }}>
              <label className="f">緊急度</label>
              <select value={form.urgency} onChange={(e) => setForm({ ...form, urgency: e.target.value as Urgency })}>
                <option>高</option>
                <option>中</option>
                <option>低</option>
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label className="f">日付</label>
              <input type="text" placeholder="2026-08-06" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
          </div>
          <label className="f">要点(ひとことで)</label>
          <textarea style={{ minHeight: 52 }} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
          <label className="f">📌 ポイント(1行に1項目)</label>
          <textarea style={{ minHeight: 70 }} value={form.points} onChange={(e) => setForm({ ...form, points: e.target.value })} />
          <label className="f">✅ やること／対応(1行に1項目)</label>
          <textarea style={{ minHeight: 70 }} value={form.actions} onChange={(e) => setForm({ ...form, actions: e.target.value })} />
          <div className="row" style={{ marginTop: 4 }}>
            <div style={{ flex: 1, minWidth: 150 }}>
              <label className="f">🎯 対象</label>
              <input type="text" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} />
            </div>
            <div style={{ flex: 1, minWidth: 150 }}>
              <label className="f">⏰ 期限</label>
              <input type="text" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} />
            </div>
          </div>
          <div className="row" style={{ marginTop: 4 }}>
            <div style={{ flex: 1, minWidth: 150 }}>
              <label className="f">☎ 連絡先</label>
              <input type="text" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} />
            </div>
            <div style={{ flex: 1, minWidth: 150 }}>
              <label className="f">差出(掲示元)</label>
              <input type="text" value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} />
            </div>
          </div>
          <label className="f">補足(任意)</label>
          <textarea style={{ minHeight: 56 }} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />

          <label className="f">📡 配信範囲</label>
          <div className="seg">
            <button className={scope === "common" ? "on" : ""} onClick={() => setScope("common")}>
              全社共通
            </button>
            <button className={scope === "targeted" ? "on" : ""} onClick={() => setScope("targeted")}>
              選択した会社のみ
            </button>
          </div>
          {scope === "targeted" && (
            <div style={{ marginTop: 10 }}>
              {companies.map((c) => (
                <label key={c.id} className="row" style={{ fontSize: 13, marginBottom: 4 }}>
                  <input type="checkbox" checked={checkedCompanyIds.has(c.id)} onChange={() => toggleCompany(c.id)} />
                  {c.name}
                </label>
              ))}
              {!companies.length && <div className="hint">「配信先(会社)」タブで会社を登録してください</div>}
            </div>
          )}

          <div className="row" style={{ marginTop: 14 }}>
            <button className="btn" onClick={handleSave} disabled={saving}>
              <span>💾</span>
              {saving ? "保存中…" : "この内容で保存"}
            </button>
            {savedMaterialId && (
              <button className="btn sec" onClick={handlePublish} disabled={publishing}>
                <span>📨</span>
                {publishing ? "配信中…" : "配信する"}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <h2 className="no-print">
          <span className="ic">📄</span>2. 資料プレビュー(印刷=PDF化)
        </h2>
        <div className="no-print row" style={{ marginBottom: 10 }}>
          <button className="btn sec sm" onClick={() => window.print()}>
            <span>🖨️</span>印刷 / PDF保存
          </button>
        </div>
        <div className="preview-wrap">
          <MaterialSheet material={{ ...preview, id: savedMaterialId ?? undefined }} />
        </div>
      </div>

      {toast && (
        <div className="toast" style={{ opacity: 1 }}>
          {toast}
        </div>
      )}
    </div>
  );
}
